/**
 * HypeFly · /erleben — QR-Kampagnen-Landingpage (CMS-driven)
 * -----------------------------------------------------------
 * Print-QR-Traffic-optimiert. Mobile-first, minimalistisch, Premium-Editorial.
 * Alle Inhalte werden aus `site_content` gelesen (Keys: erleben.hero,
 * erleben.headline, erleben.price, erleben.pillars, erleben.contact).
 *
 *  1. Statisches Media-Grid mit allen Hero-Slides gleichzeitig sichtbar.
 *     Type wird pro Item automatisch via SmartMedia dispatched.
 *  2. Zentrierte Serifen-Headline.
 *  3. Preis-Banner mit Gold-Details.
 *  4. Drei Pillar-Karten (Repeater, Icon-Mapping).
 *  5. Eingebettete Kontakt-Sektion (dunkler BG, gold Akzente) mit
 *     Kategorie-Vorwahl per Karten-Klick.
 *  6. Minimaler Footer.
 */
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
    Home as HomeIcon, HardHat, Sprout, TreeDeciduous, Users, Check,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import HFHeader from "@/components/HFHeader";
import SmartMedia from "@/components/SmartMedia";
import RichText from "@/components/RichText";
import type { MediaRef } from "@/lib/media";
import { mediaProxyUrlForced } from "@/lib/media";

/** Section-Keys, die im Frontend gerendert und daher via
 *  `erleben.KEY.enabled` global ausblendbar sind. Der Footer ist DSGVO-
 *  Pflicht (Impressum/Datenschutz-Links) und bleibt bewusst außerhalb. */
const TOGGLEABLE_KEYS = [
    "erleben.hero",
    "erleben.headline",
    "erleben.intro",
    "erleben.usp",
    "erleben.pillars",
    "erleben.benefits",
    "erleben.price",
    "erleben.closing",
] as const;

/* ---------- Fallback-Defaults (falls CMS leer / Fetch-Error) ---------- */
const DEFAULT_VIDEO_ID = "05c4bcea-ca46-485d-bfd2-b45be46c3486";
const DEFAULT_PHOTO_ID = "a0873616-2fb0-4ea7-bb8d-95e4f394012d";

/** Tile-Item: identisch mit Admin/Backend-Schema, aber `HeroCarousel` wird
 *  auf dieser Seite bewusst nicht mehr genutzt — der Type bleibt lokal. */
type HeroTile = {
    id?: string;
    media?: MediaRef;
    label?: string;
    enabled?: boolean;
};

const DEFAULT_HERO = {
    // Legacy-Felder bleiben als Notfall-Fallback (falls CMS-Items komplett leer).
    video_media_id: DEFAULT_VIDEO_ID as string | null,
    photo_media_id: DEFAULT_PHOTO_ID as string | null,
    labelLeft: "Luftaufnahme" as string | null,
    labelRight: "Innenansicht" as string | null,
    // Neue Multi-Item-Struktur (bevorzugt).
    items: [] as HeroTile[],
};
const DEFAULT_HEADLINE = {
    title: "Ihr Zuhause. Für immer erlebbar.",
    subtitle: "Ein Zuhause ist weit mehr als nur vier Wände. Es ist voller Erinnerungen, Geschichten und besonderer Momente.",
    /** Editorial-Stanzas (Fraunces, poetisch). Jede Stanza ist ein Absatz;
     *  interne `\n` werden als `<br/>` gerendert. */
    bodyStanzas: [
        "Sehen Sie sich später überall um –\nals stünden Sie wieder mittendrin.",
        "Als wäre die Zeit stehengeblieben.",
    ] as string[],
};
const DEFAULT_INTRO = {
    headline: "Aufgenommen mit einer hochspezialisierten 360°-Drohne.",
    bodyParagraphs: [
        "Anders als bei klassischen Foto- oder Videoaufnahmen entstehen bei HypeFly Innen- wie Außenaufnahmen überwiegend mit einer hochspezialisierten 360°-Drohne. Sie gleitet lautlos von Raum zu Raum, über Garten, Grundstück und aus der Luft – ohne sichtbaren Kameramann, ohne Stativ, ohne Störung.",
        "Das ergibt ein vollständiges, ungebrochenes 360°-Rundum-Erlebnis Ihres gesamten Zuhauses – so wie es wirklich ist.",
    ],
    highlightEyebrow: "Premium-Bilder",
    highlightHeadline: "Premium-Standbilder aus 8K-Videomaterial.",
    highlightBodyParagraphs: [
        "Sie erhalten nicht nur ein interaktives 360°-Video Ihres Zuhauses, sondern auf Wunsch zusätzlich ausgewählte Premium-Standbilder, die direkt aus den 8K-Aufnahmen erstellt werden.",
        "Und noch mehr: Aus Ihrem Originalvideo können Sie später jederzeit selbst weitere Bilder extrahieren und über jeden Online-Fotoservice in beliebiger Größe erstellen lassen – ob als kleine Erinnerung, als hochwertiger Druck oder als großformatiges Poster. Jeder Blickwinkel, jeder Moment – festgehalten in einer Detailschärfe, die klassische Fotografie kaum leistet.",
    ],
};
const DEFAULT_USP = {
    body: "Anders als bei gewöhnlichen Fotos oder Videos können Sie sich später frei in alle Richtungen umsehen und Ihr Zuhause immer wieder neu erleben – fast so, als würden Sie erneut hindurchgehen.",
};
const DEFAULT_BENEFITS = {
    heading: "Warum HypeFly?",
    items: [
        "Interaktive 360°-Videos statt gewöhnlicher Aufnahmen.",
        "Das komplette Zuhause – innen, außen und aus der Luft.",
        "Hochwertige Premiumqualität mit modernster 360°-Technologie.",
        "Auf Smartphone, Tablet, PC oder VR-Brille jederzeit erlebbar.",
    ],
};
const DEFAULT_CLOSING = {
    claim: "Heute aufnehmen. Für immer erleben.",
    cta: "Unverbindlich anfragen →",
};
const DEFAULT_PRICE = {
    eyebrow: "Einstiegsdokumentation",
    text: "Der Einstieg beginnt bereits bei 199 €",
    subtitle: "Kompaktes Paket · Ein Objekt · Eine Aufnahme · Ideal für erste Schritte.",
};
const DEFAULT_PILLARS = {
    items: [
        { id: "familien", icon: "Home", title: "Für Familien", body: "Erinnerungen bewahren, bevor sich Ihr Zuhause verändert.", cta: "Unverbindlich anfragen →", category: "Bestandsimmobilien" },
        { id: "neubauten", icon: "HardHat", title: "Für Neubauten & Sanierungen", body: "Den gesamten Baufortschritt dauerhaft dokumentieren.", cta: "Unverbindlich anfragen →", category: "Neubauprojekte" },
        { id: "generationen", icon: "Sprout", title: "Für kommende Generationen", body: "Ein digitales Zeitdokument, das auch Kinder und Enkel noch erleben können.", cta: "Unverbindlich anfragen →", category: "Generationenhäuser" },
    ],
};

/* ---------- Icon-Mapping für Pillar-Karten (CMS-Name → Component). ---------- */
const ICON_MAP: Record<string, LucideIcon> = {
    Home: HomeIcon,
    HardHat: HardHat,
    Sprout: Sprout,
    TreeDeciduous: TreeDeciduous,
    Users: Users,
};

/* ==================== Sub-Components ==================== */

function PillarCard({
    icon: Icon, title, body, cta, testId,
}: {
    icon: LucideIcon;
    title: string; body: string; cta: string;
    testId?: string;
}) {
    return (
        <a
            href="/#kontakt"
            data-testid={testId}
            className="group block w-full p-8 rounded-sm text-center transition-all duration-200 hover:-translate-y-0.5"
            style={{
                background: "#FFFDF9",
                boxShadow: "0 1px 3px rgba(44,42,37,0.06)",
                borderBottom: "2px solid transparent",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 8px 24px -8px rgba(44,42,37,0.15)";
                e.currentTarget.style.borderBottomColor = "#C6A962";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(44,42,37,0.06)";
                e.currentTarget.style.borderBottomColor = "transparent";
            }}
        >
            <span
                className="inline-flex items-center justify-center"
                style={{ color: "#C6A962" }}
                aria-hidden="true"
            >
                <Icon size={32} strokeWidth={1.5} />
            </span>
            <h3 className="mt-5 font-display text-2xl leading-snug" style={{ color: "hsl(var(--hf-ink))" }}>
                {title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: "hsl(var(--hf-muted))" }}>
                <RichText html={body} inline />
            </p>
            <span
                className="inline-block mt-6 text-xs tracking-[0.14em] uppercase font-medium border-b border-transparent group-hover:border-current transition-colors"
                style={{ color: "#C6A962" }}
            >
                {cta}
            </span>
        </a>
    );
}

/* ==================== Page Component ==================== */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

export default function ErlebenPage() {
    /* ---- CMS-Fetch ---- */
    const [hero, setHero] = useState(DEFAULT_HERO);
    const [headline, setHeadline] = useState(DEFAULT_HEADLINE);
    const [intro, setIntro] = useState(DEFAULT_INTRO);
    const [usp, setUsp] = useState(DEFAULT_USP);
    const [benefits, setBenefits] = useState(DEFAULT_BENEFITS);
    const [closing, setClosing] = useState(DEFAULT_CLOSING);
    const [price, setPrice] = useState(DEFAULT_PRICE);
    const [pillars, setPillars] = useState(DEFAULT_PILLARS);
    const [cmsLoaded, setCmsLoaded] = useState(false);
    // Section-Sichtbarkeit: `undefined` = default sichtbar (Bestandsdaten).
    // Nur explizites `enabled: false` blendet aus. Fetcht denselben
    // `erleben.*` Payload, extrahiert das Flag pro Section-Key.
    const [enabledMap, setEnabledMap] = useState<Record<string, boolean>>({});

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/site-content?prefix=erleben.`);
                if (!res.ok) throw new Error("cms fetch failed");
                const j = await res.json();
                const items = (j?.items ?? {}) as Record<string, unknown>;
                if (!alive) return;
                if (items["erleben.hero"]) setHero({ ...DEFAULT_HERO, ...(items["erleben.hero"] as object) });
                if (items["erleben.headline"]) setHeadline({ ...DEFAULT_HEADLINE, ...(items["erleben.headline"] as object) });
                if (items["erleben.intro"]) setIntro({ ...DEFAULT_INTRO, ...(items["erleben.intro"] as object) });
                if (items["erleben.usp"]) setUsp({ ...DEFAULT_USP, ...(items["erleben.usp"] as object) });
                if (items["erleben.benefits"]) setBenefits({ ...DEFAULT_BENEFITS, ...(items["erleben.benefits"] as object) });
                if (items["erleben.closing"]) setClosing({ ...DEFAULT_CLOSING, ...(items["erleben.closing"] as object) });
                if (items["erleben.price"]) setPrice({ ...DEFAULT_PRICE, ...(items["erleben.price"] as object) });
                if (items["erleben.pillars"]) setPillars({ ...DEFAULT_PILLARS, ...(items["erleben.pillars"] as object) });
                // Sichtbarkeits-Flags extrahieren (default = true wenn Feld fehlt).
                const map: Record<string, boolean> = {};
                for (const k of TOGGLEABLE_KEYS) {
                    const v = items[k];
                    map[k] = !(v && typeof v === "object" && !Array.isArray(v)
                        && (v as { enabled?: boolean }).enabled === false);
                }
                setEnabledMap(map);
            } catch {
                /* Silent fallback → Defaults bleiben stehen. */
            } finally {
                if (alive) setCmsLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, []);
    // Helper: `enabledMap[k] !== false` → default sichtbar, nur explizites
    // `false` blendet aus. Identische Semantik wie Home/Sylt via
    // `useSiteContent().sectionEnabled`.
    const isSectionOn = (k: string) => enabledMap[k] !== false;

    /* Sichtbare Tiles: bevorzugt Multi-Item-Schema aus dem CMS, mit
     * Legacy-Rekonstruktion aus (video_media_id, photo_media_id) für
     * Übergangs-Deployments (nach der Migration nicht mehr nötig). */
    const visibleTiles: HeroTile[] = useMemo(() => {
        const base = (hero.items || []).filter((it) => it && it.enabled !== false);
        if (base.length > 0) return base;
        const legacy: HeroTile[] = [];
        if (hero.video_media_id) legacy.push({
            id: "legacy-video",
            media: { media_id: hero.video_media_id, path: "" },
            label: hero.labelLeft ?? "Luftaufnahme",
            enabled: true,
        });
        if (hero.photo_media_id) legacy.push({
            id: "legacy-photo",
            media: { media_id: hero.photo_media_id, path: "" },
            label: hero.labelRight ?? "Innenansicht",
            enabled: true,
        });
        return legacy;
    }, [hero.items, hero.video_media_id, hero.photo_media_id, hero.labelLeft, hero.labelRight]);

    /* OG-Image: erstes Tile mit Media-ID. */
    const ogSrc = useMemo(() => {
        const first = visibleTiles[0]?.media as { media_id?: string } | undefined;
        return first?.media_id ? mediaProxyUrlForced(first.media_id) : mediaProxyUrlForced(DEFAULT_PHOTO_ID);
    }, [visibleTiles]);

    // Pillar-Filterung: nur explizit deaktivierte Items (`enabled === false`)
    // werden verborgen — Legacy-Items ohne `enabled`-Feld bleiben sichtbar
    // (Default-True-Semantik). So können einzelne Sparten (Familien / Neubauten /
    // Generationen) im Admin ausgeblendet werden, ohne die ganze Section
    // deaktivieren zu müssen.
    const pillarItemsRaw = Array.isArray(pillars.items) ? pillars.items : DEFAULT_PILLARS.items;
    const pillarItems = pillarItemsRaw.filter(
        (p) => (p as { enabled?: boolean }).enabled !== false,
    );
    void cmsLoaded;

    return (
        <div
            data-testid="erleben-page"
            className="min-h-screen"
            style={{ background: "#FDFBF7", color: "hsl(var(--hf-ink))" }}
        >
            <Helmet>
                <title>HypeFly · Erleben — 360°-Immobiliendokumentation ab 199 €</title>
                <meta name="description" content="Erleben Sie HypeFly · Premium-Immobiliendokumentation in 360°. Zeitgeist bewahren — für Familien, Neubauten und kommende Generationen. Der Einstieg beginnt bereits bei 199 €." />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="HypeFly · Erleben — 360° ab 199 €" />
                <meta property="og:description" content="Zeitgeist bewahren. 360°-Immobiliendokumentation ab 199 €." />
                <meta property="og:image" content={ogSrc} />
                <meta name="twitter:card" content="summary_large_image" />
            </Helmet>

            {/* Globale Sticky-Navbar analog zu Home/Sylt. HFHeader ist sticky
                top-0 mit ~64-72 px Höhe — der Hero unten hat pt-10 md:pt-16,
                das reicht dass die erste Kachel nicht unter der Navbar
                verschwindet. */}
            <HFHeader />

            {/* ──────── 1) Zeitgeist-Headline (an der Spitze der Seite) ──────── */}
            {/* Struktur (2026-07-29 · korrigiert):
             *   H1  · Haupt-Claim (Fraunces, groß)
             *   Lead· Emotionaler Rahmen (Jost muted) — bleibt an seiner Stelle
             *   Body· Konkrete User-Experience (Jost, Ink) — aus USP eingemergt
             * Der Lead beschreibt "was ein Zuhause bedeutet" (philosophisch),
             * der Body macht es erlebbar ("was Sie später tun"). Emotional →
             * funktional. Backwards-Compat: fehlende Felder werden übersprungen. */}
            {isSectionOn("erleben.headline") && (
            <section className="px-6 pt-6 md:pt-10 pb-8 md:pb-12 text-center hf-fade-in" data-testid="erleben-headline">
                <h1
                    className="font-display leading-[1.05] text-[40px] md:text-[64px] lg:text-[72px]"
                    style={{ color: "hsl(var(--hf-ink))" }}
                    data-testid="erleben-headline-title"
                >
                    {headline.title}
                </h1>
                {(headline as { subtitle?: string }).subtitle && (
                    <p
                        className="mt-3 md:mt-4 font-display italic text-lg md:text-2xl max-w-2xl mx-auto leading-[1.45]"
                        style={{ color: "hsl(var(--hf-ink) / 0.75)" }}
                        data-testid="erleben-headline-lead"
                    >
                        <RichText html={(headline as { subtitle: string }).subtitle} inline />
                    </p>
                )}
                {(() => {
                    /* Editorial-Stanza-Layout (Fraunces, poetisch).
                     * Bevorzugt `bodyStanzas` (Array); Fallback: legacy `body`
                     * (String) → einzelne Stanza. Interne `\n` werden als
                     * `<br/>` gerendert, damit jede Zeile klar bricht. */
                    const raw = headline as {
                        bodyStanzas?: string[]; body?: string;
                    };
                    const stanzas = Array.isArray(raw.bodyStanzas) && raw.bodyStanzas.length
                        ? raw.bodyStanzas
                        : (raw.body ? [raw.body] : []);
                    if (stanzas.length === 0) return null;
                    return (
                        <div
                            className="mt-3 md:mt-4 max-w-2xl mx-auto space-y-3 md:space-y-4"
                            data-testid="erleben-headline-body"
                        >
                            {stanzas.map((s, i) => {
                                const lines = s.split("\n");
                                return (
                                    <p
                                        key={i}
                                        className="font-display text-lg md:text-2xl leading-[1.45] italic"
                                        style={{ color: "hsl(var(--hf-ink))" }}
                                        data-testid={`erleben-headline-body-p-${i}`}
                                    >
                                        {lines.map((ln, j) => (
                                            <span key={j}>
                                                {ln}
                                                {j < lines.length - 1 && <br />}
                                            </span>
                                        ))}
                                    </p>
                                );
                            })}
                        </div>
                    );
                })()}
            </section>
            )}

            {/* ──────── 2) Statisches Media-Grid (kein Karussell) ──────── */}
            {isSectionOn("erleben.hero") && (
            <section className="pt-4 md:pt-6 pb-8 md:pb-12 px-6" data-testid="erleben-hero">
                <div className="max-w-5xl mx-auto">
                    <div
                        className="grid grid-cols-1 md:grid-cols-2 gap-6"
                        data-testid="erleben-hero-grid"
                    >
                        {visibleTiles.map((tile, idx) => {
                            const mid = (tile.media as { media_id?: string } | undefined)?.media_id;
                            const key = tile.id || `tile-${idx}`;
                            return (
                                <figure
                                    key={key}
                                    className="flex flex-col gap-3"
                                    data-testid={`erleben-hero-tile-${idx}`}
                                >
                                    <div
                                        className="hf-erleben-frame hf-erleben-tile relative w-full overflow-hidden rounded-md"
                                        style={{
                                            aspectRatio: "4/3",
                                            background: "#F1EBDD",
                                            border: "1.5px solid rgba(180,155,100,0.6)",
                                            boxShadow: [
                                                "0 4px 24px rgba(198,169,98,0.10)",
                                                "inset 0 0 12px rgba(198,169,98,0.05)",
                                            ].join(", "),
                                        }}
                                    >
                                        {mid ? (
                                            <SmartMedia
                                                mediaRef={{ media_id: mid, path: "" } as MediaRef}
                                                className="absolute inset-0 w-full h-full object-cover"
                                                interactive360
                                                autoTour360={true}
                                                autoPlay360={true}
                                                videoAutoplay
                                                videoMuted
                                                videoLoop
                                                videoControls={false}
                                                showTypeBadge={false}
                                                loading="eager"
                                                testId={`erleben-hero-tile-${idx}-media`}
                                            />
                                        ) : (
                                            <div
                                                className="absolute inset-0 flex items-center justify-center text-xs"
                                                style={{ color: "hsl(var(--hf-muted))" }}
                                            >
                                                Kein Medium hinterlegt.
                                            </div>
                                        )}
                                    </div>
                                    {tile.label && (
                                        <figcaption
                                            className="text-center uppercase tracking-[0.20em] text-xs font-medium"
                                            style={{ color: "hsl(var(--hf-muted))" }}
                                            data-testid={`erleben-hero-tile-${idx}-caption`}
                                        >
                                            {tile.label}
                                        </figcaption>
                                    )}
                                </figure>
                            );
                        })}
                    </div>
                </div>
            </section>
            )}

            {/* ──────── 3) Intro · Editorial-Absatz mit 8K-Standbild-Highlight ──────── */}
            {isSectionOn("erleben.intro") && (
            <section className="px-6 py-12 md:py-20" data-testid="erleben-intro">
                <div className="max-w-3xl mx-auto">
                    {/* Headline · Fraunces */}
                    {(() => {
                        const headline = (intro as { headline?: string }).headline
                            ?? DEFAULT_INTRO.headline;
                        return headline ? (
                            <h2
                                className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] mb-6 md:mb-8"
                                style={{ color: "hsl(var(--hf-ink))" }}
                                data-testid="erleben-intro-headline"
                            >
                                {headline}
                            </h2>
                        ) : null;
                    })()}
                    {/* Fließtext · 2 Paragraphen aus `bodyParagraphs`.
                     * Backwards-Compat: legacy string `body` → als 1-Item-Array. */}
                    {(() => {
                        const raw = intro as {
                            bodyParagraphs?: string[]; body?: string;
                        };
                        const paras = Array.isArray(raw.bodyParagraphs) && raw.bodyParagraphs.length
                            ? raw.bodyParagraphs
                            : (raw.body ? [raw.body] : []);
                        return (
                            <div className="space-y-5 md:space-y-6" data-testid="erleben-intro-body">
                                {paras.map((p, i) => (
                                    <p
                                        key={i}
                                        className="text-lg md:text-xl leading-[1.6]"
                                        style={{ color: "hsl(var(--hf-ink))" }}
                                        data-testid={`erleben-intro-body-p-${i}`}
                                    >
                                        {p}
                                    </p>
                                ))}
                            </div>
                        );
                    })()}
                    {/* Highlight-Box (Gold-Border) · Premium-Standbilder aus 8K.
                     * Body ist jetzt ein Paragraph-Array (`highlightBodyParagraphs`).
                     * Backwards-Compat: legacy strings `highlightBody`
                     * (single) und `highlight` (allererste Version) werden als
                     * 1-Item-Array übernommen. */}
                    {(() => {
                        const raw = intro as {
                            highlightEyebrow?: string;
                            highlightHeadline?: string;
                            highlightBodyParagraphs?: string[];
                            highlightBody?: string;
                            highlight?: string;
                        };
                        const eyebrow = raw.highlightEyebrow ?? DEFAULT_INTRO.highlightEyebrow;
                        const headline = raw.highlightHeadline ?? raw.highlight ?? "";
                        const paras = Array.isArray(raw.highlightBodyParagraphs)
                            && raw.highlightBodyParagraphs.length
                            ? raw.highlightBodyParagraphs
                            : (raw.highlightBody ? [raw.highlightBody] : []);
                        if (!headline && paras.length === 0) return null;
                        return (
                            <aside
                                data-testid="erleben-intro-highlight"
                                className="mt-10 md:mt-14 p-6 md:p-8 relative"
                                style={{
                                    border: "1px solid #C6A962",
                                    background: "rgba(198,169,98,0.06)",
                                }}
                            >
                                {eyebrow && (
                                    <div
                                        className="hf-eyebrow mb-3"
                                        style={{ color: "#8F7A44", fontSize: 10 }}
                                        data-testid="erleben-intro-highlight-eyebrow"
                                    >
                                        {eyebrow}
                                    </div>
                                )}
                                {headline && (
                                    <h3
                                        className="font-display text-2xl md:text-3xl leading-[1.25] mb-4"
                                        style={{ color: "hsl(var(--hf-ink))" }}
                                        data-testid="erleben-intro-highlight-headline"
                                    >
                                        {headline}
                                    </h3>
                                )}
                                {paras.length > 0 && (
                                    <div
                                        className="space-y-4 md:space-y-5"
                                        data-testid="erleben-intro-highlight-body"
                                    >
                                        {paras.map((p, i) => (
                                            <p
                                                key={i}
                                                className="text-base md:text-lg leading-[1.6]"
                                                style={{ color: "hsl(var(--hf-ink))" }}
                                                data-testid={`erleben-intro-highlight-body-p-${i}`}
                                            >
                                                {p}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </aside>
                        );
                    })()}
                </div>
            </section>
            )}

            {/* ──────── 4) USP · Freies Umsehen ──────── */}
            {isSectionOn("erleben.usp") && (
            <section className="px-6 py-12 md:py-20" data-testid="erleben-usp">
                <div className="max-w-3xl mx-auto">
                    {(() => {
                        const raw = usp as { headline?: string; body?: string };
                        const headline = raw.headline ?? "";
                        const body = raw.body ?? "";
                        return (
                            <>
                                {headline && (
                                    <h2
                                        className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] mb-6 md:mb-8"
                                        style={{ color: "hsl(var(--hf-ink))" }}
                                        data-testid="erleben-usp-headline"
                                    >
                                        {headline}
                                    </h2>
                                )}
                                {body && (
                                    <p
                                        className="text-lg md:text-xl leading-[1.6]"
                                        style={{ color: "hsl(var(--hf-ink))" }}
                                        data-testid="erleben-usp-body"
                                    >
                                        <RichText html={body} inline />
                                    </p>
                                )}
                            </>
                        );
                    })()}
                </div>
            </section>
            )}

            {/* ──────── 5) Preis-Banner (vor den Pillars – User-Reihenfolge 2026-07-29) ──────── */}
            {isSectionOn("erleben.price") && (
            <section className="px-6 py-16 md:py-24" data-testid="erleben-price">
                <div
                    className="max-w-2xl mx-auto py-8 px-6 md:px-10 text-center"
                    style={{
                        background: "#FDFBF7",
                        borderBottom: "2px solid #C6A962",
                        boxShadow: "0 1px 3px rgba(44,42,37,0.06)",
                    }}
                >
                    <div className="text-[11px] tracking-[0.24em] uppercase mb-3" style={{ color: "#C6A962", fontWeight: 600 }}>
                        {price.eyebrow}
                    </div>
                    <p className="font-display text-[28px] md:text-[36px] leading-tight" style={{ color: "hsl(var(--hf-ink))" }}>
                        {price.text}
                    </p>
                    <p className="mt-3 text-xs md:text-sm" style={{ color: "hsl(var(--hf-muted))" }}>
                        {price.subtitle}
                    </p>
                </div>
            </section>
            )}

            {/* ──────── 6) Drei-Säulen (Zielgruppen) ──────── */}
            {isSectionOn("erleben.pillars") && pillarItems.length > 0 && (
            <section className="px-6 py-16 md:py-24" data-testid="erleben-pillars">
                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                    {pillarItems.map((p, i) => {
                        const Ico = ICON_MAP[p.icon] ?? HomeIcon;
                        return (
                            <PillarCard
                                key={p.id ?? i}
                                icon={Ico}
                                title={p.title}
                                body={p.body}
                                cta={p.cta}
                                testId={`erleben-card-${p.id ?? i}`}
                            />
                        );
                    })}
                </div>
            </section>
            )}

            {/* ──────── 7) Warum HypeFly? · Vorteils-Bullets ──────── */}
            {isSectionOn("erleben.benefits") && (
            <section className="px-6 py-16 md:py-24" data-testid="erleben-benefits">
                <div className="max-w-3xl mx-auto">
                    <h2
                        className="font-display text-3xl md:text-5xl leading-tight text-center"
                        style={{ color: "hsl(var(--hf-ink))" }}
                        data-testid="erleben-benefits-heading"
                    >
                        {benefits.heading}
                    </h2>
                    <ul
                        className="mt-10 md:mt-12 space-y-5 md:space-y-6"
                        data-testid="erleben-benefits-list"
                    >
                        {(Array.isArray(benefits.items) ? benefits.items : []).map((line, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-4 md:gap-5"
                                data-testid={`erleben-benefits-item-${i}`}
                            >
                                <span
                                    aria-hidden="true"
                                    className="inline-flex items-center justify-center shrink-0 mt-1"
                                    style={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: "50%",
                                        border: "1px solid #C6A962",
                                        color: "#C6A962",
                                        background: "rgba(198,169,98,0.08)",
                                    }}
                                >
                                    <Check size={14} strokeWidth={2.4} />
                                </span>
                                <span
                                    className="text-base md:text-lg leading-relaxed"
                                    style={{ color: "hsl(var(--hf-ink))" }}
                                >
                                    {line}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
            )}

            {/* ──────── 8) Closing · Finaler Claim + CTA ──────── */}
            {isSectionOn("erleben.closing") && (
            <section className="px-6 py-16 md:py-24 text-center" data-testid="erleben-closing">
                <div className="max-w-3xl mx-auto">
                    <p
                        className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.15]"
                        style={{ color: "hsl(var(--hf-ink))" }}
                        data-testid="erleben-closing-claim"
                    >
                        {closing.claim}
                    </p>
                    <a
                        href="/#kontakt"
                        data-testid="erleben-closing-cta"
                        className="inline-block mt-10 md:mt-12 px-8 md:px-10 py-4 text-xs tracking-[0.20em] uppercase font-medium transition-colors"
                        style={{
                            background: "hsl(var(--hf-ink))",
                            color: "#FDFBF7",
                            borderBottom: "2px solid #C6A962",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#3D3A34";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = "hsl(var(--hf-ink))";
                        }}
                    >
                        {closing.cta}
                    </a>
                </div>
            </section>
            )}

            {/* ──────── 9) Footer ──────── */}
            <footer
                className="py-8 px-6 text-center text-xs"
                style={{ color: "hsl(var(--hf-muted))", borderTop: "1px solid rgba(216,205,191,0.4)" }}
                data-testid="erleben-footer"
            >
                <div className="font-display text-base mb-3" style={{ color: "hsl(var(--hf-ink))" }}>HypeFly</div>
                <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                    <Link to="/impressum" className="hover:text-hf-ink transition-colors">Impressum</Link>
                    <Link to="/datenschutz" className="hover:text-hf-ink transition-colors">Datenschutz</Link>
                    <Link to="/" className="hover:text-hf-ink transition-colors">Zur Startseite</Link>
                </div>
                <div className="mt-4 text-[11px] tracking-[0.14em] uppercase">Eine Marke der BDV GmbH.</div>
            </footer>
        </div>
    );
}
